/**
 * Created by jonas on 2022-06-15.
 */
import {gsap} from "gsap";
import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {TheCage} from "../../TheCage";
import {TheCageSymbol} from "../../reels/TheCageSymbol";
import {TheCageStatics} from "../../config/TheCageStatics";
import {XBombSymbol} from "../../reels/XBombSymbol";
import {GSAPUtils} from "../../utils/GSAPUtils";
import {TheCageSound} from "../../sound/TheCageSound";


export class FreeSpinTransformFeature implements SimplePresentation {
    name:string = "FreeSpinTransformFeature";

    constructor() {
    }

    private transformTarget1() {
        return (<TheCageSymbol>TheCage.freeSpinReelView.avalancheMatrixModel.getSymbolAt({x:3, y:1}).positionable).symbolName;
    }

    private transformTarget2() {
        return (<TheCageSymbol>TheCage.freeSpinReelView.avalancheMatrixModel.getSymbolAt({x:5, y:1}).positionable).symbolName;
    }

    private shouldRunTransform1() {
        return (TheCage.model.data.featuresActive[1] &&
            TheCage.model.data.featuresUsedBefore[1] != TheCage.model.data.featuresUsedAfter[1] &&
            TheCageStatics.isLowSymbol(this.transformTarget1()));
    }

    private shouldRunTransform2() {
        return (TheCage.model.data.featuresActive[2] &&
            TheCage.model.data.featuresUsedBefore[2] != TheCage.model.data.featuresUsedAfter[2] &&
            TheCageStatics.isLowSymbol(this.transformTarget2()));
    }

    private newSymbol() {
        return ["M3", "M2", "M1"][TheCage.model.data.fsLevel - 2];
    }

    private newSymbolLevel() {
        return TheCage.model.data.fsLevel - 2;
    }

    private preTransformSymbols(targetSymbolName: string, centerReel: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        const symbolPositions = TheCage.freeSpinReelView.avalancheMatrixModel.getAllSymbolsWithName(targetSymbolName);
        const preTransforms:gsap.core.Timeline[][] = [[], [], [], [], [], [], []];

        // highlight the color of the mid symbol they will turn into
        for(let sym of symbolPositions) {
            const transformTl = gsap.timeline();
            transformTl.add((<TheCageSymbol>sym.positionable).preTransformAnimation(this.newSymbolLevel()));
            preTransforms[Math.abs(centerReel - sym.reelId)].push(transformTl);
        }
        const preTransformReelTimelines = preTransforms.filter((transformTl) => transformTl.length > 0)
            .map((transformTl) => GSAPUtils.createTimelineFromArray(transformTl, '<'));
        tl.add(GSAPUtils.createTimelineFromArray(preTransformReelTimelines, '<0.1'), );

        tl.add(() => {}, '>0.1');

        return tl;
    }

    private transformSymbols(targetSymbolName: string[], centerReel: number): gsap.core.Timeline {
        const symbolPositions = TheCage.freeSpinReelView.avalancheMatrixModel.getAllSymbolsWithNames(targetSymbolName);
        const transforms:gsap.core.Timeline[][] = [[], [], [], [], [], [], []];
        const symbolsToDestroy:TheCageSymbol[] = [];
        const tl = gsap.timeline();

        // transform the symbols one by one
        for(let sym of symbolPositions) {
            const transformTl = gsap.timeline();
            const data = TheCage.freeSpinReelView.transformSymbolAt(sym.reelId, sym.reelPosition, this.newSymbol());
            const newSym = (<TheCageSymbol>data.newSymbol.positionable);
            const oldSym = (<TheCageSymbol>data.oldSymbol.positionable);
            newSym.alpha = 0;

            if(TheCage.model.data.symbolSizes[sym.reelId][sym.reelPosition] > 1) {
                transformTl.add(newSym.setMultiplierInstant());
            }
            transformTl.add(newSym.transformAnimation(this.newSymbolLevel()));
            transformTl.add(gsap.to(newSym, {duration : 0.2, alpha : 1}), 0);
            transformTl.add(gsap.to(oldSym, {duration : 0.2, alpha : 0}), 0);

            symbolsToDestroy.push(oldSym);
            transforms[Math.abs(centerReel - sym.reelId)].push(transformTl);
        }

        const reelTimelines = transforms.filter((transformTl) => transformTl.length > 0)
                                        .map((transformTl) => GSAPUtils.createTimelineFromArray(transformTl, '<'));
        tl.add(GSAPUtils.createTimelineFromArray(reelTimelines, '<0.1'), );
        tl.add(() => {
            //Destroy old Transform symbols.
            for(let symToDestroy of symbolsToDestroy) {
                symToDestroy.parent.removeChild(symToDestroy);
                symToDestroy.destroy();
            }
        });
        return tl;
    }

    shouldRun():boolean {
        return this.shouldRunTransform1() || this.shouldRunTransform2();
    }

    start(controller:SimplePresentationRunnerController):void {

        const tl:gsap.core.Timeline = gsap.timeline({onComplete:() => {
                controller.finish();
            }});
        tl.add(() => controller.setSkipCallback(() => {
            tl.timeScale(2) ;
        }));

        const targetSymbpols:string[] = [];
        const isBigWin = TheCage.model.data.totalSpinWinnings > 20;

        if (this.transformTarget1() == this.transformTarget2() && this.shouldRunTransform1() && this.shouldRunTransform2()) {
            tl.add(TheCage.freeSpinReelView.features.useTransform1());
            tl.add(TheCage.freeSpinReelView.features.useTransform2(), '<');
            tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_TRANSFORM)}, '<');
            tl.add(this.preTransformSymbols(this.transformTarget1(), 4));
            tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_TRANSFORM_2)}, '<');
            if (isBigWin) {
                tl.add(() => {}, '>1.3');
            }
            targetSymbpols.push(this.transformTarget1());
        } else {
            if (this.shouldRunTransform1()) {
                tl.add(TheCage.freeSpinReelView.features.useTransform1());
                tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_TRANSFORM)}, '<');
                tl.add(this.preTransformSymbols(this.transformTarget1(), 3));
                tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_TRANSFORM_2)}, '<');
                targetSymbpols.push(this.transformTarget1());
            }

            if (this.shouldRunTransform2()) {
                tl.add(TheCage.freeSpinReelView.features.useTransform2());
                tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_TRANSFORM)}, '<');
                tl.add(this.preTransformSymbols(this.transformTarget2(), 5));
                tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_TRANSFORM_2)}, '<');
                targetSymbpols.push(this.transformTarget2());
            }
            if (isBigWin) {
                tl.add(() => {}, '>1.3');
            }
        }
        tl.add(this.transformSymbols(targetSymbpols, 4));
        tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_TRANSFORM_3)}, '<');
        if (isBigWin) {
            tl.add(() => {}, '>1.3');
        } else {
            tl.add(() => {}, '>0.2');
        }
    }
}
